import type { BlobProvisionResult, VercelRunner } from '@cli-types/next-adapter/vercel.js'
import { spinner } from '@cli-utils/terminal.js'
import { createStoreQuiet } from './create-store-quiet.js'
import { deleteOrphanStore } from './delete-orphan-store.js'
import { isEnvVarConflict } from './is-env-var-conflict.js'
import { pullBlobToken } from './pull-blob-token.js'

export async function provisionBlobStore(
  runner: VercelRunner,
  cwd: string,
  options: { name: string; env?: NodeJS.ProcessEnv }
): Promise<BlobProvisionResult> {
  const provisionSpinner = spinner()
  provisionSpinner.start('Creating your Vercel Blob store')

  const { result, storeName } = await createStoreQuiet(runner, cwd, options.name, options.env)

  if (!result.success && isEnvVarConflict(result)) {
    await deleteOrphanStore(runner, cwd, result, options.env)
    provisionSpinner.message('Retrieving the Blob read-write token')
    const token = await pullBlobToken(runner, cwd, options.env)
    if (!token) {
      provisionSpinner.stop('No BLOB_READ_WRITE_TOKEN came back from Vercel')
      return { failure: 'env-pull-empty' }
    }
    provisionSpinner.clear()
    return { token }
  }

  if (!result.success) {
    provisionSpinner.stop('Vercel Blob provisioning did not complete')
    const combined = `${result.stdout}\n${result.stderr}`.trim()
    return {
      failure: result.timedOut ? 'timeout' : 'provision-failed',
      detail: combined || undefined
    }
  }

  provisionSpinner.message('Retrieving the Blob read-write token')
  const token = await pullBlobToken(runner, cwd, options.env)
  if (!token) {
    provisionSpinner.stop('No BLOB_READ_WRITE_TOKEN came back from Vercel')
    return { storeName, failure: 'env-pull-empty' }
  }

  provisionSpinner.clear()
  return { token, storeName }
}
